import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import store from "@/store";
import router from "@/router";
import { getAuthSessionManager } from "@/utils/authSessionManager";

const SHARED_LOGOUT_MARKER_APPLIED_MESSAGE = "Your session ended in another VaultN app.";

function isUnauthorizedApiError(error: unknown): boolean {
  return axios.isAxiosError(error) && error.response?.status === 401;
}

function isAccountDisabledError(error: unknown): boolean {
  return axios.isAxiosError(error) && error.response?.headers?.["x-account-disabled"] === "true";
}

async function routeToUnauthenticatedFlow(): Promise<void> {
  if (router.currentRoute.value.path === "/") {
    return;
  }

  try {
    await router.replace({ path: "/" });
  } catch {
    // Navigation may already be in progress; local auth state is already cleared.
  }
}

export default class ApiService {
  private axiosInstance: AxiosInstance;

  constructor() {
    this.axiosInstance = axios.create({
      baseURL: process.env.VUE_APP_API_URL,
      withCredentials: false,
    });
  }

  async axiosCall<T>(config: AxiosRequestConfig) {
    try {
      store.state.loadingSpinner = true;
      if (await getAuthSessionManager().applySharedLogoutMarkerIfNeeded()) {
        throw new Error(SHARED_LOGOUT_MARKER_APPLIED_MESSAGE);
      }

      const { data }: AxiosResponse =
        await this.axiosInstance.request<T>(config);
      store.state.loadingSpinner = false;
      return [null, data];
    } catch (error) {
      if (isAccountDisabledError(error)) {
        await getAuthSessionManager().handleManualLogout();
      } else if (isUnauthorizedApiError(error)) {
        await getAuthSessionManager().handleUnauthorizedApiResponse();
        await routeToUnauthenticatedFlow();
      }

      store.state.loadingSpinner = false;
      return [error, null];
    }
  }
}

export const service = new ApiService();
